import { useState, useRef, useCallback, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  uploadToStorage,
  removeFromStorage,
  SUPPORTED_IMAGE_FORMATS,
  SUPPORTED_VIDEO_FORMATS,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
} from "../utils/fileUpload";
import type { Creative } from "../types";

/* -------------------------------------------------------------------------- */
/*                          useGeneralCreativeUpload                          */
/* -------------------------------------------------------------------------- */

/**
 * General (campaign-level) creative upload – one file at a time.
 */
export const useGeneralCreativeUpload = () => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [creatives, setCreatives] = useState<Creative[]>(() => {
    try {
      const saved = localStorage.getItem("creatives");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Persist
  useEffect(() => {
    localStorage.setItem("creatives", JSON.stringify(creatives));
  }, [creatives]);

  /* ------------------------------ Validation ------------------------------ */

  const validateFile = useCallback(
    (file: File): boolean => {
      const isImage = SUPPORTED_IMAGE_FORMATS.includes(file.type);
      const isVideo = SUPPORTED_VIDEO_FORMATS.includes(file.type) || file.type.startsWith("video/");

      if (!isImage && !isVideo) {
        toast({
          title: "Unsupported file type",
          description: "Please upload an image (JPG, PNG, GIF) or video (MP4, MOV).",
          variant: "destructive",
        });
        return false;
      }

      if (isImage && file.size > MAX_IMAGE_SIZE) {
        toast({
          title: "Image too large",
          description: "Images must be smaller than 10 MB.",
          variant: "destructive",
        });
        return false;
      }

      if (isVideo && file.size > MAX_VIDEO_SIZE) {
        toast({
          title: "Video too large",
          description: "Videos must be smaller than 100 MB.",
          variant: "destructive",
        });
        return false;
      }

      return true;
    },
    [toast],
  );

  /* ------------------------------- Sanitize -------------------------------- */

  const sanitize = (name: string) =>
    name
      .replace(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g, "")
      .replace(/[^\w.-]/g, "_")
      .replace(/_{2,}/g, "_")
      .replace(/^_+|_+$/g, "") || "creative";

  /* -------------------------------- Upload -------------------------------- */

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      if (creatives.length >= 1) {
        toast({
          title: "Limit reached",
          description: "Only one creative is allowed. Remove the existing one first.",
          variant: "destructive",
        });
        return;
      }

      const file = files[0];
      if (!validateFile(file)) return;

      setIsUploading(true);
      setUploadProgress(0);

      try {
        const fileName = `${Date.now()}-${sanitize(file.name)}`;
        const url = await uploadToStorage(file, fileName, setUploadProgress);

        setCreatives([{ name: file.name, type: file.type, size: file.size, url }]);
        toast({ title: "Uploaded", description: `${file.name} uploaded successfully.` });
      } catch (err) {
        console.error("[generalUpload] Error:", err);
        toast({
          title: "Upload failed",
          description: err instanceof Error ? err.message : "Unknown error – please retry.",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [creatives.length, toast, validateFile],
  );

  /* -------------------------------- Remove -------------------------------- */

  const removeCreative = useCallback(
    async (index: number) => {
      const creative = creatives[index];
      if (!creative) return;

      setIsUploading(true);

      try {
        // Only remove from storage if it's not an external link
        if (creative.url && !creative.isExternalLink) {
          await removeFromStorage(creative.url);
        }
        setCreatives([]);
      } catch (err) {
        console.error("[generalUpload] Remove error:", err);
        toast({
          title: "Remove failed",
          description: err instanceof Error ? err.message : "Could not remove file.",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [creatives, toast],
  );

  /* ------------------------------ Area Click ------------------------------ */

  const handleAreaClick = useCallback(() => {
    if (creatives.length >= 1) {
      toast({
        title: "Limit reached",
        description: "Remove the existing creative before uploading a new one.",
        variant: "destructive",
      });
      return;
    }
    fileInputRef.current?.click();
  }, [creatives.length, toast]);

  /* ------------------------------ URL Submit ------------------------------ */

  const handleUrlSubmit = useCallback(
    (url: string) => {
      if (creatives.length >= 1) {
        toast({
          title: "Limit reached",
          description: "Only one creative is allowed. Remove the existing one first.",
          variant: "destructive",
        });
        return;
      }

      // Extract a display name from the URL
      let name = 'External Link';
      try {
        const urlObj = new URL(url);
        // Use hostname as name for clarity
        name = urlObj.hostname.replace('www.', '');
      } catch {
        // Keep default name if URL parsing fails
      }

      setCreatives([{ 
        name, 
        type: 'link', 
        size: 0,
        url,
        isExternalLink: true,
      }]);
      
      toast({ title: "Added", description: `Link added successfully.` });
    },
    [creatives.length, toast],
  );

  /* ------------------------------ Return API ------------------------------ */

  return {
    creatives,
    isUploading,
    uploadProgress,
    fileInputRef,
    handleFileChange,
    removeCreative,
    handleAreaClick,
    handleUrlSubmit,
  };
};
